import React, { useState, useEffect } from 'react';
import {
  FolderPlus,
  FolderTree,
  Folder,
  Folders,
  Package,
  ListTree,
  ChevronsDown,
  ChevronsUp,
  ChevronDown,
  FolderOpen,
  Edit,
  Trash2,
  X,
  Save,
  FileText,
  Settings,
  AlertTriangle,
} from 'lucide-react';
import styles from './CategoriePage.module.css';
import categorieService, { CategoriaRequest } from '../../services/categorieService';
import { Categoria } from '../../types';
import { ExportDropdown, ExportFormat } from '@/components/common/ExportDropdown';
import { exportToExcel, exportToPdf, ExportColumn } from '@/utils/exports';

// Types
interface Sottocategoria {
  id: number;
  nome: string;
  articoli: number;
  parentId?: number;
  tipo: 'entrata' | 'uscita' | 'entrambi';
  colore?: string;
  descrizione?: string;
  attiva: boolean;
}

interface CategoriaDisplay {
  id: number;
  nome: string;
  color: string;
  subcategorieCount: number;
  articoliCount: number;
  badgeBg: string;
  badgeColor: string;
  codice?: string;
  descrizione?: string;
  tipo: 'entrata' | 'uscita' | 'entrambi';
  attiva: boolean;
  sottocategorie: Sottocategoria[];
}

// Helper functions
const getCategoryColor = (tipo: 'entrata' | 'uscita' | 'entrambi'): { color: string; badgeBg: string; badgeColor: string } => {
  switch (tipo) {
    case 'entrata':
      return {
        color: 'var(--success-main)',
        badgeBg: 'var(--success-bg)',
        badgeColor: 'var(--success-main)',
      };
    case 'uscita':
      return {
        color: 'var(--error-main)',
        badgeBg: 'var(--error-bg)',
        badgeColor: 'var(--error-main)',
      };
    case 'entrambi':
      return {
        color: 'var(--primary-main)',
        badgeBg: 'var(--primary-light)',
        badgeColor: 'var(--primary-main)',
      };
    default:
      return {
        color: 'var(--text-secondary)',
        badgeBg: 'var(--bg-secondary)',
        badgeColor: 'var(--text-secondary)',
      };
  }
};

const transformCategorieToDisplay = (categorie: Categoria[]): CategoriaDisplay[] => {
  // Separate main categories (without parentId) and subcategories
  const mainCategories = categorie.filter(cat => !cat.parentId);
  const subcategories = categorie.filter(cat => cat.parentId);

  return mainCategories.map(cat => {
    const subs = subcategories.filter(sub => sub.parentId === cat.id);
    const colors = getCategoryColor(cat.tipo);

    return {
      id: cat.id,
      nome: cat.nome,
      color: cat.colore || colors.color,
      subcategorieCount: subs.length,
      articoliCount: 0, // TODO: This should come from backend
      badgeBg: colors.badgeBg,
      badgeColor: colors.badgeColor,
      codice: cat.nome.substring(0, 3).toUpperCase(),
      descrizione: cat.descrizione,
      tipo: cat.tipo,
      attiva: cat.attiva,
      sottocategorie: subs.map(sub => ({
        id: sub.id,
        nome: sub.nome,
        articoli: 0, // TODO: This should come from backend
        parentId: sub.parentId,
        tipo: sub.tipo,
        colore: sub.colore,
        descrizione: sub.descrizione,
        attiva: sub.attiva,
      })),
    };
  });
};

export const CategoriePage: React.FC = () => {
  // Data states
  const [categorie, setCategorie] = useState<Categoria[]>([]);
  const [categorieDisplay, setCategorieDisplay] = useState<CategoriaDisplay[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // UI states
  const [expandedCategories, setExpandedCategories] = useState<number[]>([]);

  // Modal states
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [editingCategory, setEditingCategory] = useState<Categoria | null>(null);
  const [editingSubcategory, setEditingSubcategory] = useState<Sottocategoria | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<{ type: 'categoria' | 'sottocategoria'; item: Categoria | Sottocategoria } | null>(null);

  // Form states
  const [tipoCategoria, setTipoCategoria] = useState('principale');
  const [formData, setFormData] = useState({
    nome: '',
    codice: '',
    colore: 'Blu',
    descrizione: '',
    parentId: '',
    entrata: false,
    uscita: false,
    attiva: true,
  });
  const [formErrors, setFormErrors] = useState<{nome?: boolean; parentId?: boolean}>({});
  const [exportLoading, setExportLoading] = useState(false);

  // Load categories on mount
  useEffect(() => {
    loadCategorie();
  }, []);

  // Load categories from backend
  const loadCategorie = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await categorieService.getCategorie();
      setCategorie(data);
      const displayData = transformCategorieToDisplay(data);
      setCategorieDisplay(displayData);
      // Auto-expand all categories on initial load
      setExpandedCategories(displayData.map(c => c.id));
    } catch (err) {
      setError('Errore nel caricamento delle categorie');
      console.error('Error loading categories:', err);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (categoryId: number) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const expandAll = () => {
    setExpandedCategories(categorieDisplay.map((c) => c.id));
  };

  const collapseAll = () => {
    setExpandedCategories([]);
  };

  const handleTipoCategoriaChange = (value: string) => {
    setTipoCategoria(value);
  };

  // Open modal for new category
  const openNewCategoryModal = () => {
    setEditingCategory(null);
    setEditingSubcategory(null);
    setTipoCategoria('principale');
    setFormData({
      nome: '',
      codice: '',
      colore: 'Blu',
      descrizione: '',
      parentId: '',
      entrata: false,
      uscita: false,
      attiva: true,
    });
    setShowFormModal(true);
  };

  // Open modal for editing category
  const openEditCategoryModal = (categoriaId: number) => {
    const categoria = categorie.find(c => c.id === categoriaId);
    if (!categoria) return;

    setEditingCategory(categoria);
    setEditingSubcategory(null);
    setTipoCategoria(categoria.parentId ? 'sottocategoria' : 'principale');
    setFormData({
      nome: categoria.nome,
      codice: categoria.nome.substring(0, 3).toUpperCase(),
      colore: categoria.colore || 'Blu',
      descrizione: categoria.descrizione || '',
      parentId: categoria.parentId?.toString() || '',
      entrata: categoria.tipo === 'entrata' || categoria.tipo === 'entrambi',
      uscita: categoria.tipo === 'uscita' || categoria.tipo === 'entrambi',
      attiva: categoria.attiva,
    });
    setShowFormModal(true);
  };

  // Open modal for editing subcategory
  const openEditSubcategoryModal = (subId: number) => {
    const sub = categorie.find(c => c.id === subId);
    if (!sub) return;

    setEditingCategory(null);
    setEditingSubcategory({
      id: sub.id,
      nome: sub.nome,
      articoli: 0,
      parentId: sub.parentId,
      tipo: sub.tipo,
      colore: sub.colore,
      descrizione: sub.descrizione,
      attiva: sub.attiva,
    });
    setTipoCategoria('sottocategoria');
    setFormData({
      nome: sub.nome,
      codice: '',
      colore: sub.colore || 'Blu',
      descrizione: sub.descrizione || '',
      parentId: sub.parentId?.toString() || '',
      entrata: sub.tipo === 'entrata' || sub.tipo === 'entrambi',
      uscita: sub.tipo === 'uscita' || sub.tipo === 'entrambi',
      attiva: sub.attiva,
    });
    setShowFormModal(true);
  };

  // Open delete confirmation
  const openDeleteModal = (type: 'categoria' | 'sottocategoria', itemId: number) => {
    const item = categorie.find(c => c.id === itemId);
    if (!item) return;
    setDeleteTarget({ type, item });
    setShowDeleteModal(true);
  };

  // Handle form save
  const handleSave = async () => {
    // Validate required fields
    const errors: {nome?: boolean; parentId?: boolean} = {};
    if (!formData.nome.trim()) errors.nome = true;
    if ((tipoCategoria === 'sottocategoria' || editingSubcategory) && !formData.parentId) errors.parentId = true;

    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      return;
    }

    try {
      setLoading(true);

      // Determine tipo based on checkboxes or inherit from parent for subcategories
      let tipo: 'ENTRATA' | 'USCITA' | 'ENTRAMBI';
      if (tipoCategoria === 'sottocategoria' || editingSubcategory) {
        // For subcategories, inherit tipo from parent
        const parentId = formData.parentId ? parseInt(formData.parentId) : editingSubcategory?.parentId;
        const parent = categorie.find(c => c.id === parentId);
        if (parent) {
          tipo = parent.tipo.toUpperCase() as 'ENTRATA' | 'USCITA' | 'ENTRAMBI';
        } else {
          // Fallback: use checkboxes or default
          tipo = formData.entrata && formData.uscita ? 'ENTRAMBI' : formData.entrata ? 'ENTRATA' : 'USCITA';
        }
      } else if (formData.entrata && formData.uscita) {
        tipo = 'ENTRAMBI';
      } else if (formData.entrata) {
        tipo = 'ENTRATA';
      } else if (formData.uscita) {
        tipo = 'USCITA';
      } else {
        // Default: ENTRAMBI if neither checkbox is selected
        tipo = 'ENTRAMBI';
      }

      const requestData: CategoriaRequest = {
        nome: formData.nome,
        tipo: tipo,
        descrizione: formData.descrizione || undefined,
        colore: formData.colore || undefined,
        isActive: formData.attiva,
        parentId: formData.parentId ? parseInt(formData.parentId) : undefined,
      };

      if (editingCategory) {
        // Update existing category
        await categorieService.updateCategoria(editingCategory.id, requestData);
      } else if (editingSubcategory) {
        // Update existing subcategory
        await categorieService.updateCategoria(editingSubcategory.id, requestData);
      } else {
        // Create new category
        await categorieService.createCategoria(requestData);
      }

      // Reload categories
      await loadCategorie();
      setFormErrors({});
      setShowFormModal(false);
    } catch (err) {
      setError('Errore nel salvataggio della categoria');
      console.error('Error saving category:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle delete confirm
  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;

    try {
      setLoading(true);
      await categorieService.deleteCategoria(deleteTarget.item.id);

      // Reload categories
      await loadCategorie();
      setShowDeleteModal(false);
      setDeleteTarget(null);
    } catch (err) {
      setError('Errore nell\'eliminazione della categoria');
      console.error('Error deleting category:', err);
    } finally {
      setLoading(false);
    }
  };

  // Handle export
  const handleExport = async (format: ExportFormat) => {
    if (categorie.length === 0) {
      alert('Nessun dato da esportare');
      return;
    }

    setExportLoading(true);
    try {
      const columns: ExportColumn[] = [
        { field: 'nome', header: 'Nome', type: 'text', width: 25 },
        { field: 'codice', header: 'Codice', type: 'text', width: 10 },
        { field: 'tipoDisplay', header: 'Tipo', type: 'text', width: 12 },
        { field: 'colore', header: 'Colore', type: 'text', width: 10 },
        { field: 'attivaDisplay', header: 'Attiva', type: 'text', width: 8 },
        { field: 'parentNome', header: 'Categoria Padre', type: 'text', width: 20 },
      ];

      // Build flat list with indentation for subcategories
      const exportData = categorie.map(cat => {
        const parent = cat.parentId ? categorie.find(c => c.id === cat.parentId) : null;
        const isSubcategory = !!cat.parentId;
        return {
          nome: isSubcategory ? `  └ ${cat.nome}` : cat.nome,
          codice: cat.nome.substring(0, 3).toUpperCase(),
          tipoDisplay: cat.tipo === 'entrata' ? 'Entrata' : cat.tipo === 'uscita' ? 'Uscita' : 'Entrambi',
          colore: cat.colore || '',
          attivaDisplay: cat.attiva ? 'Sì' : 'No',
          parentNome: parent?.nome || '',
        };
      });

      const config = {
        title: 'Categorie',
        filename: 'categorie',
        columns,
        orientation: 'portrait' as const,
      };

      if (format === 'excel') {
        await exportToExcel(config, exportData);
      } else {
        await exportToPdf(config, exportData);
      }
    } catch (err) {
      console.error('Error exporting categorie:', err);
      setError('Errore durante l\'esportazione');
    } finally {
      setExportLoading(false);
    }
  };

  const isEditing = editingCategory !== null || editingSubcategory !== null;

  // Calculate stats from real data
  const totalCategorie = categorieDisplay.length;
  const totalSottocategorie = categorieDisplay.reduce((sum, cat) => sum + cat.subcategorieCount, 0);
  const totalArticoli = categorieDisplay.reduce((sum, cat) => sum + cat.articoliCount, 0);

  return (
    <div className={styles.container}>
      <main>
        {/* Page Header */}
        <div className={styles.pageHeader}>
          <h1 className={styles.pageTitle}>
            <FolderTree size={32} style={{ verticalAlign: 'middle', marginRight: '12px' }} />
            Reparti
          </h1>
          <div className={styles.pageActions}>
            <ExportDropdown
              onExport={handleExport}
              disabled={categorie.length === 0}
              loading={exportLoading}
            />
            <button className={styles.btnPrimary} onClick={openNewCategoryModal} disabled={loading}>
              <FolderPlus size={18} />
              Nuovo Reparto
            </button>
          </div>
        </div>

        {/* Error message */}
        {error && (
          <div style={{
            padding: '12px',
            marginBottom: '16px',
            backgroundColor: 'var(--error-bg)',
            color: 'var(--error-main)',
            borderRadius: '8px',
            display: 'flex',
            alignItems: 'center',
            gap: '8px'
          }}>
            <AlertTriangle size={20} />
            {error}
          </div>
        )}

        {/* Stats Bar - Compact horizontal summary */}
        <div className={styles.statsBar}>
          <div className={styles.statItem}>
            <Folder size={20} style={{ color: 'var(--primary-main)' }} />
            <span className={styles.statNumber}>{loading ? '...' : totalCategorie}</span>
            <span className={styles.statText}>Reparti</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <Folders size={20} style={{ color: 'var(--info-main)' }} />
            <span className={styles.statNumber}>{loading ? '...' : totalSottocategorie}</span>
            <span className={styles.statText}>Sottoreparti</span>
          </div>
          <div className={styles.statDivider} />
          <div className={styles.statItem}>
            <Package size={20} style={{ color: 'var(--success-main)' }} />
            <span className={styles.statNumber}>{loading ? '...' : totalArticoli}</span>
            <span className={styles.statText}>Articoli</span>
          </div>
        </div>

        {/* Content Grid */}
        <div className={`${styles.grid} ${styles.grid2}`} style={{ gap: '24px' }}>
          <div className={styles.card} style={{ gridColumn: 'span 2' }}>
            <div className={styles.cardHeader}>
              <h3 className={styles.cardTitle}>
                <ListTree size={20} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                Struttura Reparti
              </h3>
              <div style={{ display: 'flex', gap: '12px' }}>
                <button className={styles.btnSecondarySmall} onClick={expandAll}>
                  <ChevronsDown size={16} />
                  Espandi Tutto
                </button>
                <button className={styles.btnSecondarySmall} onClick={collapseAll}>
                  <ChevronsUp size={16} />
                  Chiudi Tutto
                </button>
              </div>
            </div>

            {/* Loading state */}
            {loading && (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                Caricamento reparti...
              </div>
            )}

            {/* Empty state */}
            {!loading && categorieDisplay.length === 0 && (
              <div style={{ textAlign: 'center', padding: '40px', color: 'var(--text-secondary)' }}>
                <FolderTree size={48} style={{ opacity: 0.3, marginBottom: '16px' }} />
                <div>Nessun reparto trovato</div>
                <button
                  className={styles.btnPrimary}
                  onClick={openNewCategoryModal}
                  style={{ marginTop: '16px' }}
                >
                  <FolderPlus size={18} />
                  Crea Primo Reparto
                </button>
              </div>
            )}

            {/* Categories */}
            {!loading && categorieDisplay.map((categoria) => (
              <div
                key={categoria.id}
                className={styles.categoryItem}
                style={{ borderLeft: `4px solid ${categoria.color}` }}
              >
                <div
                  className={styles.categoryHeader}
                  onClick={() => toggleCategory(categoria.id)}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <ChevronDown
                      size={20}
                      style={{
                        color: 'var(--text-secondary)',
                        transition: 'transform 0.2s',
                        transform: expandedCategories.includes(categoria.id)
                          ? 'rotate(0deg)'
                          : 'rotate(-90deg)',
                      }}
                    />
                    <Folder size={24} style={{ color: categoria.color }} />
                    <div>
                      <div className={`${styles.bodyBase} ${styles.fw600}`}>{categoria.nome}</div>
                      <div className={styles.bodySmall} style={{ color: 'var(--text-secondary)' }}>
                        {categoria.subcategorieCount} sottoreparti
                      </div>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <span
                      className={styles.badge}
                      style={{
                        background: categoria.badgeBg,
                        color: categoria.badgeColor,
                      }}
                    >
                      {categoria.articoliCount} articoli
                    </span>
                    <button
                      className={styles.btnIcon}
                      onClick={(e) => {
                        e.stopPropagation();
                        openEditCategoryModal(categoria.id);
                      }}
                      title="Modifica reparto"
                    >
                      <Edit size={16} />
                    </button>
                    <button
                      className={styles.btnIcon}
                      onClick={(e) => {
                        e.stopPropagation();
                        openDeleteModal('categoria', categoria.id);
                      }}
                      title="Elimina reparto"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
                <div
                  className={`${styles.categoryDetails} ${
                    expandedCategories.includes(categoria.id) ? '' : styles.collapsed
                  }`}
                >
                  {categoria.sottocategorie.map((sub) => (
                    <div key={sub.id} className={styles.subcategoryItem}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <FolderOpen size={20} style={{ color: 'var(--text-secondary)' }} />
                        <div>
                          <div className={`${styles.bodySmall} ${styles.fw500}`}>{sub.nome}</div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <span
                          className={styles.bodyXsmall}
                          style={{ color: 'var(--text-secondary)' }}
                        >
                          {sub.articoli} articoli
                        </span>
                        <button
                          className={styles.btnIcon}
                          onClick={() => openEditSubcategoryModal(sub.id)}
                          title="Modifica sottoreparto"
                        >
                          <Edit size={14} />
                        </button>
                        <button
                          className={styles.btnIcon}
                          onClick={() => openDeleteModal('sottocategoria', sub.id)}
                          title="Elimina sottoreparto"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Modal Form Categoria */}
      {showFormModal && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowFormModal(false); }}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>
                {isEditing ? <Edit size={24} /> : <FolderPlus size={24} />}
                <span style={{ marginLeft: '8px' }}>
                  {isEditing
                    ? editingSubcategory
                      ? 'Modifica Sottoreparto'
                      : 'Modifica Reparto'
                    : 'Nuovo Reparto'}
                </span>
              </h3>
              <button className={styles.modalClose} onClick={() => setShowFormModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className={styles.modalBody}>
              {/* Tipo Categoria - solo per nuove */}
              {!isEditing && (
                <div className={styles.mb6}>
                  <label className={styles.formLabel}>Tipo *</label>
                  <div className={styles.radioGroupBorder}>
                    <label className={styles.radioWrapperBorder}>
                      <input
                        type="radio"
                        name="tipo_categoria"
                        value="principale"
                        className={styles.radio}
                        checked={tipoCategoria === 'principale'}
                        onChange={(e) => handleTipoCategoriaChange(e.target.value)}
                      />
                      <Folder size={16} style={{ color: 'var(--primary-main)' }} />
                      <span>Reparto Principale</span>
                    </label>
                    <label className={styles.radioWrapperBorder}>
                      <input
                        type="radio"
                        name="tipo_categoria"
                        value="sottocategoria"
                        className={styles.radio}
                        checked={tipoCategoria === 'sottocategoria'}
                        onChange={(e) => handleTipoCategoriaChange(e.target.value)}
                      />
                      <FolderOpen size={16} style={{ color: 'var(--info-main)' }} />
                      <span>Sottoreparto</span>
                    </label>
                  </div>
                </div>
              )}

              {/* Dati Categoria */}
              <div className={styles.section}>
                <h4 className={styles.sectionHeader}>
                  <FileText size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                  Informazioni
                </h4>
                <div className={`${styles.grid} ${styles.grid2}`}>
                  <div className={styles.formGroup} style={{ gridColumn: 'span 2' }}>
                    <label className={styles.formLabel}>Nome *</label>
                    <input
                      type="text"
                      className={`${styles.formControl} ${formErrors.nome ? styles.error : ''}`}
                      placeholder="Es: Beauty linea corpo"
                      value={formData.nome}
                      onChange={(e) => { setFormData({ ...formData, nome: e.target.value }); setFormErrors(prev => ({...prev, nome: false})); }}
                    />
                    {formErrors.nome && <span className={styles.errorText}>Campo obbligatorio</span>}
                  </div>
                  {(tipoCategoria === 'sottocategoria' || editingSubcategory) && (
                    <div className={styles.formGroup} style={{ gridColumn: 'span 2' }}>
                      <label className={styles.formLabel}>Reparto Principale *</label>
                      <select
                        className={`${styles.formControl} ${formErrors.parentId ? styles.error : ''}`}
                        value={formData.parentId}
                        onChange={(e) => { setFormData({ ...formData, parentId: e.target.value }); setFormErrors(prev => ({...prev, parentId: false})); }}
                      >
                        <option value="">Seleziona reparto principale...</option>
                        {categorieDisplay.map(cat => (
                          <option key={cat.id} value={cat.id}>{cat.nome}</option>
                        ))}
                      </select>
                      {formErrors.parentId && <span className={styles.errorText}>Campo obbligatorio</span>}
                    </div>
                  )}
                  {tipoCategoria === 'principale' && !editingSubcategory && (
                    <>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Codice</label>
                        <input
                          type="text"
                          className={styles.formControl}
                          placeholder="Es: CAT-001"
                          value={formData.codice}
                          onChange={(e) => setFormData({ ...formData, codice: e.target.value })}
                        />
                      </div>
                      <div className={styles.formGroup}>
                        <label className={styles.formLabel}>Colore</label>
                        <select
                          className={styles.formControl}
                          value={formData.colore}
                          onChange={(e) => setFormData({ ...formData, colore: e.target.value })}
                        >
                          <option>Blu</option>
                          <option>Verde</option>
                          <option>Rosso</option>
                          <option>Arancione</option>
                          <option>Viola</option>
                          <option>Grigio</option>
                        </select>
                      </div>
                    </>
                  )}
                  <div className={styles.formGroup} style={{ gridColumn: 'span 2' }}>
                    <label className={styles.formLabel}>Descrizione</label>
                    <textarea
                      className={styles.formControl}
                      rows={3}
                      placeholder="Descrizione..."
                      value={formData.descrizione}
                      onChange={(e) => setFormData({ ...formData, descrizione: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              {/* Impostazioni - solo per categorie principali */}
              {tipoCategoria === 'principale' && !editingSubcategory && (
                <div className={styles.section}>
                  <h4 className={styles.sectionHeader}>
                    <Settings size={16} style={{ verticalAlign: 'middle', marginRight: '6px' }} />
                    Impostazioni
                  </h4>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <label className={styles.checkboxWrapper}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={formData.entrata}
                        onChange={(e) => setFormData({ ...formData, entrata: e.target.checked })}
                      />
                      <span>Utilizzata per movimenti in entrata</span>
                    </label>
                    <label className={styles.checkboxWrapper}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={formData.uscita}
                        onChange={(e) => setFormData({ ...formData, uscita: e.target.checked })}
                      />
                      <span>Utilizzata per movimenti in uscita</span>
                    </label>
                    <label className={styles.checkboxWrapper}>
                      <input
                        type="checkbox"
                        className={styles.checkbox}
                        checked={formData.attiva}
                        onChange={(e) => setFormData({ ...formData, attiva: e.target.checked })}
                      />
                      <span>Reparto attivo</span>
                    </label>
                  </div>
                </div>
              )}

              <div className={styles.bodyXsmall} style={{ color: 'var(--text-secondary)', marginTop: '16px' }}>
                * Campi obbligatori
              </div>
            </div>
            <div className={styles.modalFooter}>
              <button className={styles.btnSecondary} onClick={() => setShowFormModal(false)}>
                Annulla
              </button>
              <button className={styles.btnPrimary} onClick={handleSave}>
                <Save size={16} />
                {isEditing ? 'Salva Modifiche' : 'Crea Reparto'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Conferma Eliminazione */}
      {showDeleteModal && deleteTarget && (
        <div className={styles.modalOverlay} onMouseDown={(e) => { if (e.target === e.currentTarget) setShowDeleteModal(false); }}>
          <div className={styles.modalConfirm} onClick={(e) => e.stopPropagation()}>
            <div className={styles.confirmIcon}>
              <AlertTriangle size={48} />
            </div>
            <h3 className={styles.confirmTitle}>Conferma Eliminazione</h3>
            <p className={styles.confirmText}>
              Sei sicuro di voler eliminare {deleteTarget.type === 'categoria' ? 'il reparto' : 'il sottoreparto'}{' '}
              <strong>"{deleteTarget.item.nome}"</strong>?
              {deleteTarget.type === 'categoria' && (
                <><br /><span className={styles.confirmWarning}>Tutti i sottoreparti associati verranno eliminati.</span></>
              )}
            </p>
            <div className={styles.confirmActions}>
              <button className={styles.btnSecondary} onClick={() => setShowDeleteModal(false)}>
                Annulla
              </button>
              <button className={styles.btnDanger} onClick={handleDeleteConfirm}>
                <Trash2 size={16} />
                Elimina
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
